import type { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";

// The site's standard centred, width-constrained page column. Making every
// content column a <ContentColumn> gives it a stable `data-draw-anchor` handle
// for the annotation layer (see DrawingLayer): a saved drawing re-resolves to
// the same content at any width via that handle, instead of the layer
// reverse-engineering a fragile nth-of-type CSS path that breaks when the
// responsive DOM differs from draw time.
//
// Pass a unique, stable `anchor` per column on a page (e.g. "home-hero"). It
// must not change with the viewport, so ink stays on the content across
// breakpoints; keep it stable across edits so old drawings keep resolving.
//
// This is intentionally a thin wrapper: it forwards `className` verbatim (so a
// column renders exactly as the raw <div> did) and only adds the data attribute.
// Use `as` to render a different element than <div>.
type ContentColumnProps<T extends ElementType> = {
  anchor: string;
  as?: T;
  children?: ReactNode;
} & Omit<ComponentPropsWithoutRef<T>, "as" | "children">;

export function ContentColumn<T extends ElementType = "div">({ anchor, as, children, ...rest }: ContentColumnProps<T>) {
  const Tag = (as ?? "div") as ElementType;
  return (
    <Tag data-draw-anchor={anchor} {...rest}>
      {children}
    </Tag>
  );
}
